// The chrome every signed-in screen sits inside.
//
// Before this, each private route rendered a bare fragment under one long
// horizontal nav written out longhand in App.tsx. Now there is one shell and
// the pages are its content, which is what makes the dashboard and the composer
// read as two rooms in one building rather than two buildings.
//
//   ┌───────────────────────────────────────────────┐
//   │ header: brand · favourites · search · account │
//   ├────┬──────────────┬───────────────────────────┤
//   │rail│   sidebar    │        page content       │
//   └────┴──────────────┴───────────────────────────┘
//
// Three widths, three behaviours. On a desktop the rail and the sidebar are
// both beside the page, and collapsing removes the sidebar column outright —
// the rail stays, because it says which accounts this workspace publishes to,
// and it carries the control that brings the menu back. A tablet starts
// collapsed for the room. On a phone the sidebar becomes a drawer over the page
// and the favourites move into the header, because a three-column layout
// squeezed into 390px is four columns of nothing.

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import type { Session } from "@supabase/supabase-js";
import { useWorkspace } from "../lib/WorkspaceContext";
import { useConnections } from "../lib/useConnections";
import { useBrands } from "../lib/useBrands";
import { useEntitlements } from "../lib/useEntitlements";
import { COMPANY } from "../lib/company";
import { AppHeader } from "./AppHeader";
import { CollapsibleSidebar } from "./CollapsibleSidebar";
import { FavoritesRail } from "./FavoritesRail";
import { useFavourites, useFavouriteCandidates, useSidebar } from "./state";
import type { NavContext } from "./nav";

/** Which of the three layouts is live. One listener, not a resize handler. */
function useViewport(): { mobile: boolean; tablet: boolean } {
  const [state, setState] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return { mobile: false, tablet: false };
    }
    return {
      mobile: window.matchMedia("(max-width: 860px)").matches,
      tablet: window.matchMedia("(max-width: 1180px)").matches,
    };
  });

  useEffect(() => {
    if (!window.matchMedia) return;
    const phone = window.matchMedia("(max-width: 860px)");
    const pad = window.matchMedia("(max-width: 1180px)");
    const sync = () => setState({ mobile: phone.matches, tablet: pad.matches });
    sync();
    phone.addEventListener("change", sync);
    pad.addEventListener("change", sync);
    return () => {
      phone.removeEventListener("change", sync);
      pad.removeEventListener("change", sync);
    };
  }, []);

  return state;
}

export function AppShell({
  session, banners, children, toolbarExtras,
}: {
  session: Session;
  /** Anything that stands above the page on every screen — an invitation
   *  waiting, the onboarding strip. Passed in rather than imported, so the
   *  engagement module keeps its one importer (ADR-0016). */
  banners?: ReactNode;
  toolbarExtras?: ReactNode;
  children: ReactNode;
}) {
  // Which business am I acting in, and what may I do in it? Both come from one
  // place (ADR-0011); the server checks the answer again on every call.
  const { workspaces, active, select, can, ready, error } = useWorkspace();
  const workspaceId = active?.workspace_id ?? null;

  // Memoised because it keys the favourites list: a fresh object each render
  // would rebuild what this person may see on every keystroke anywhere.
  const ctx: NavContext = useMemo(
    () => ({ can, kind: active?.kind ?? null }),
    [can, active?.kind],
  );

  const { connections, ready: connectionsReady } = useConnections(workspaceId);
  // Which brand is being looked at. Held here rather than on the Connections
  // screen because the switcher is in the chrome, and the two must not be able
  // to disagree about the answer (ADR-0029).
  const { brands, activeBrandId, selectBrand } = useBrands(workspaceId);
  const { entitlements } = useEntitlements(workspaceId);
  // Two surfaces, two lists, one set of candidates. See `state.ts` for why they
  // stopped being one.
  const candidates = useFavouriteCandidates(ctx, connections);
  const topFavourites = useFavourites(workspaceId, "top", candidates);
  const railFavourites = useFavourites(workspaceId, "rail", candidates);
  const { expanded, toggle, setExpanded } = useSidebar();
  const { mobile, tablet } = useViewport();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Whether the toggle has been pressed since this tab opened. It is the
  // difference between "we collapsed this for you because the window is narrow"
  // and "you asked for it collapsed" — without it the toggle is a dead control
  // at tablet widths, which is worse than no automatic collapse at all.
  const [decided, setDecided] = useState(false);
  const location = useLocation();

  // A tablet starts without the menu column and gets the width back — until
  // somebody says otherwise. The stored preference is never overwritten by the
  // window size: widen the window and the sidebar comes back the way it was
  // left.
  const navOpen = mobile ? drawerOpen : tablet && !decided ? false : expanded;

  // Arriving somewhere closes the drawer. Without this, tapping a link on a
  // phone navigates behind a menu that is still covering the result.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Widening the window past the drawer breakpoint has to leave the navigation
  // in a state somebody can see, rather than a closed drawer nothing opens.
  useEffect(() => {
    if (!mobile) setDrawerOpen(false);
  }, [mobile]);



  // One handler for both toggles — the one in the header and the one in the
  // rail. They are the same control in two places, not two behaviours.
  const toggleNav = () => {
    if (mobile) { setDrawerOpen((on) => !on); return; }
    // On a tablet the first press has to act on what is on screen, not on a
    // stored preference the automatic collapse is currently overriding.
    if (tablet && !decided) { setDecided(true); setExpanded(!navOpen); return; }
    setDecided(true);
    toggle();
  };

  return (
    <div className={`app-shell${mobile ? " is-mobile" : ""}`}
         data-sidebar={navOpen ? "open" : "closed"}>
      <a href="#app-content" className="skip-link">Skip to content</a>

      <AppHeader
        toolbarExtras={toolbarExtras}
        session={session}
        workspaces={workspaces}
        active={active}
        onSelectWorkspace={select}
        brands={brands}
        activeBrandId={activeBrandId}
        onSelectBrand={selectBrand}
        favourites={topFavourites}
        connectionsLoading={!connectionsReady}
        navOpen={navOpen}
        onToggleNav={toggleNav}
        navControls={mobile ? "drawer" : "sidebar"}
      />

      <div className="app-body">
        {/* Never hidden by the sidebar's state — that is the whole contract of
            this column, and the reason the way back from a collapsed menu is
            on it. It goes only on a phone, where the favourites are in the
            header instead. */}
        {!mobile && (
          <FavoritesRail
            rail={railFavourites}
            accountsConnected={connections.some((c) => c.status === "active")}
            loading={!connectionsReady}
            navOpen={navOpen}
            onToggleNav={toggleNav}
          />
        )}

        {mobile && drawerOpen && (
          <div className="sidebar-backdrop" onClick={() => setDrawerOpen(false)} aria-hidden="true" />
        )}

        {navOpen && (
          <CollapsibleSidebar
            ctx={ctx}
            drawer={mobile}
            onNavigate={() => setDrawerOpen(false)}
            // The sidebar only renders while it is open, so "close" and
            // "toggle" are the same act from in there.
            onClose={toggleNav}
            entitlements={entitlements}
            plan={active?.plan ?? null}
          />
        )}

        <div className="app-content" id="app-content">
          {/* Permissions decide which controls exist at all, so failing to load
              them looks exactly like a missing feature — buttons simply are not
              there. Say so instead of leaving people hunting for an Upload
              button that the page decided they may not have. */}
          {ready && error && (
            <div className="card err workspace-error">
              <strong>Your workspace and permissions could not be loaded.</strong>{" "}
              Until this clears, anything you do not have a confirmed permission for is hidden —
              including uploading, publishing and connecting accounts.
              <div className="muted" style={{ marginTop: ".4rem", fontSize: ".85rem" }}>{error}</div>
            </div>
          )}
          {ready && !error && !active && (
            <div className="card err workspace-error">
              You are signed in but not a member of any workspace, so there is nothing to upload to.
            </div>
          )}

          {banners}

          <main className="app-main">{children}</main>

          <footer className="app-footer">
            <span className="muted">&copy; {COMPANY.effectiveDate.slice(-4)} {COMPANY.legalName}</span>
            <span className="spacer" />
            <Link to="/support">Support</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/data-deletion">Delete your data</Link>
            <Link to="/security">Security</Link>
          </footer>
        </div>
      </div>

    </div>
  );
}
